import { useState, type PropsWithChildren, useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { WLink, WLinkButton } from '@alice-health/wonderland-react'
import {
  WIconComponentProps,
  WLinkButtonComponentProps
} from '@alice-health/wonderland'

import type { ClassName, Wvariant, WLinkVariant } from '@/types'
import { makeUrlWithUtms } from '@/utils/query-params'
import { DispatchEventProperties } from '@/utils/analytics-events/types'
import { dispatchEvent } from '@/utils/analytics-events'

export interface AppLinkBaseProps {
  id?: string
  href: string
  variant?: WLinkVariant | Wvariant
  quiet?: boolean
  iconButton?: boolean
  icon?: WIconComponentProps['icon']
  iconPosition?: WLinkButtonComponentProps['iconPosition']
  size?: 'small' | 'medium'
  as: 'link' | 'button'
  target?: '_self' | '_parent' | '_blank' | '_top'
  className?: ClassName
  withoutUtms?: boolean
  block?: boolean
  hash?: string
  query?: URLSearchParams
  onClick?: () => void
  event?: DispatchEventProperties
}
export interface WAppLinkBaseProps extends AppLinkBaseProps {
  as: 'link'
  variant?: WLinkVariant
  quiet?: boolean
  iconButton?: undefined
  iconPosition?: undefined
}

export interface WAppLinkButtonBaseProps extends AppLinkBaseProps {
  as: 'button'
  variant?: Wvariant
  quiet?: undefined
  iconButton?: boolean
  iconPosition?: WLinkButtonComponentProps['iconPosition']
}

export type AppLinkProps = PropsWithChildren<
  WAppLinkBaseProps | WAppLinkButtonBaseProps
>
export const AppLink = ({
  id,
  as,
  variant,
  iconButton = false,
  children,
  href,
  withoutUtms = false,
  hash,
  query,
  onClick,
  className,
  event,
  ...linkProps
}: AppLinkProps) => {
  const searchParams = useSearchParams()
  /**
   * use setMounted for avoid 'Uncaught Error: Minified React error #418'
   * Ref: https://legacy.reactjs.org/docs/error-decoder.html/?invariant=418
   */
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  // return this null to avoid hydration errors
  if (!mounted) {
    return null
  }

  const url = () => {
    let url = ''

    if (withoutUtms) {
      url = query ? `${href}?${query.toString()}` : href
    } else {
      url = makeUrlWithUtms({ href, query, searchParams })
    }

    url = hash ? `${url}#${hash}` : url
    return url
  }

  const handleClick = () => {
    if (event) dispatchEvent(event)
    onClick && onClick()
  }

  if (as === 'button') {
    return (
      <Link
        id={id}
        data-testid={id}
        href={url()}
        passHref
        target={linkProps.target}
        className={className}
        onClick={handleClick}
      >
        <WLinkButton
          {...linkProps}
          variant={variant}
          iconButton={iconButton}
          icon-position={linkProps.iconPosition}
          block={linkProps.block}
        >
          {children}
        </WLinkButton>
      </Link>
    )
  }

  return (
    <Link
      id={id}
      data-testid={id}
      href={url()}
      passHref
      target={linkProps.target}
      className={className}
      onClick={handleClick}
    >
      {/* @ts-expect-error -  prop passHref forces Link to send the href property to its child */}
      <WLink
        data-testid={`${id}s`}
        {...linkProps}
        variant={variant}
      >
        {children}
      </WLink>
    </Link>
  )
}
